import React from 'react';
import { Report } from '../../../../redux/reports/api/Report.model';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Stack,
  Link,
} from '@mui/material';
import ChannelsMapTableView from './ChannelsMapTable';
import { getPolyglot } from '../../../../i18n';
import moment from 'moment';

export interface ReportDetailProps {
  report: Report;
  groupId?: string;
}

const formatDate = (date: string) => moment(date).format('L');

const ReportDetail: React.FC<ReportDetailProps> = ({ report, groupId }) => {
  const polyglot = getPolyglot();
  return (
    <Box p={1}>
      <Typography variant="h4" gutterBottom>
        {report.reportTitle}
      </Typography>
      <Grid container direction="row" spacing={1} mb={2}>
        <Grid item>
          <Chip label={`${polyglot.t('general.id')}: ${report.id}`} />
        </Grid>
        <Grid item>
          <Chip
            label={`${polyglot.t('reports.state')}: ${report.executionState}`}
            color={
              report.executionState === 'COMPLETED'
                ? 'success'
                : report.executionState === 'ERROR'
                ? 'error'
                : report.executionState === 'GENERATING' ||
                  report.executionState === 'RENDERING' ||
                  report.executionState === 'STORING'
                ? 'warning'
                : undefined
            }
          />
        </Grid>
        {report.reportScheduleId && (
          <Grid item>
            <Chip
              label={`${polyglot.t('reports.schedule.title')}: ${
                report.reportScheduleId
              }`}
            />
          </Grid>
        )}
        {report.reportType && (
          <Grid item>
            <Chip
              label={`${polyglot.t('reports.type.title')}: ${
                report.reportType?.name ?? report.reportTypeId
              }`}
            />
          </Grid>
        )}

        {(report?.intervalTimeInSeconds ?? 0) > 0 && (
          <Grid item>
            <Chip
              label={`${polyglot.t('reports.interval_sample')}: ${
                report.intervalTimeInSeconds ?? '-'
              }`}
            />
          </Grid>
        )}
        {report?.labels?.temperatureUnitPreferred && (
          <Grid item>
            <Chip
              label={`${polyglot.t('user.temperature_unit')}: ${
                report.labels.temperatureUnitPreferred == 'DEGREEFAHRENHEIT'
                  ? '°F'
                  : '°C'
              }`}
            />
          </Grid>
        )}
        {/* <Grid item>
          <Chip
            label={`${polyglot.t('reports.sequence') || 'Sequence'}: ${
              report.sequenceReportValue
            }`}
          />
        </Grid> */}
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2">
            {polyglot.t('timeframe_selection_panel.time_range')}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Chip
              label={`${polyglot.t('reports.schedule.from')}: ${formatDate(
                report.timeRange.from
              )}`}
            />
            <Chip
              label={`${polyglot.t('reports.schedule.to')}: ${formatDate(
                report.timeRange.to
              )}`}
            />
          </Stack>
        </Grid>
      </Grid>

      <Divider sx={{ my: 1 }} />

      <Typography variant="h6" gutterBottom>
        {polyglot.t('reports.channel_map.label')}
      </Typography>
      <ChannelsMapTableView groupId={groupId} report={report} />

      <Typography variant="h6" gutterBottom>
        {polyglot.t('reports.formats')}
      </Typography>
      <Stack direction="row" spacing={2} mb={2}>
        {Object.entries(report.files)
          .filter(([_, url]) => url)
          .map(([fmt, url]) =>
            url ? (
              <Link
                key={fmt}
                href={url}
                target="_blank"
                rel="noopener"
                underline="hover"
              >
                <Chip label={fmt.toUpperCase()} color="primary" clickable />
              </Link>
            ) : (
              <Chip key={fmt} label={fmt.toUpperCase()} variant="outlined" />
            )
          )}
      </Stack>
    </Box>
  );
};

export default ReportDetail;
